import { pageContainer, pageHeading, pageSubtitle, emptyFrame, type as text, theme } from "../../theme";
import { SheetCard, DimensionHeading } from "../../components/Drawing";
import { LoadingBlock } from "../../components/Spinner";
import { useFrameworks } from "../../context/FrameworksContext";
import { AttentionList, MS_LABELS, useMsEnrolment, type MsFrameworkId } from "./shared";
import { useMsOverview } from "./useRegister";
import { ContextRegister } from "./ContextRegister";
import { ObjectivesRegister } from "./ObjectivesRegister";
import { AuditProgramme } from "./AuditProgramme";
import { ManagementReviews } from "./ManagementReviews";
import { CompetenceRegister } from "./CompetenceRegister";
import { AspectsRegister } from "./AspectsRegister";
import { HazardsRegister } from "./HazardsRegister";
import { ObligationsRegister } from "./ObligationsRegister";

// The management system registers (docs/MANAGEMENT-SYSTEM.md): the records ISO harmonised-structure
// clauses 4–10 ask every management-system standard to keep. One route per register, each a tab
// of the Management system area (nav/clientNav.ts); the overview gathers what needs attention.

export type MsTab = "overview" | "context" | "objectives" | "audits" | "reviews" | "competence" | "aspects" | "hazards" | "obligations";

const TITLES: Record<MsTab, { title: string; subtitle: string }> = {
  overview: { title: "Management system", subtitle: "The records every ISO management-system audit tests, and what needs doing on them." },
  context: { title: "Context and interested parties", subtitle: "The issues that affect what the management system must achieve, the interested parties, their requirements and which of them you address (clauses 4.1 and 4.2)." },
  objectives: { title: "Objectives", subtitle: "What the management system sets out to achieve, how each objective is measured, who owns it and the results recorded (clauses 6.2 and 9.1)." },
  audits: { title: "Internal audits", subtitle: "The audit programme, each audit's scope, auditor and conclusion, and the findings that become corrective actions (clause 9.2)." },
  reviews: { title: "Management review", subtitle: "Top management's reviews: the inputs each standard requires, the conclusion and the decisions taken (clause 9.3)." },
  competence: { title: "Competence", subtitle: "The competence each role needs and the evidence that the people in it hold it (clause 7.2)." },
  aspects: { title: "Environmental aspects", subtitle: "Aspects of your activities, products and services, their environmental impacts and which are significant (ISO 14001 clause 6.1.2)." },
  hazards: { title: "OH&S hazards", subtitle: "Hazards of routine and non-routine work, who they affect, and the risk before and after controls (ISO 45001 clause 6.1.2)." },
  obligations: { title: "Compliance obligations", subtitle: "The legal and other requirements you must or choose to meet, and the evaluation of compliance with each (clauses 6.1.3 and 9.1.2)." },
};

export function ManagementSystemPage({ tab }: { tab: MsTab }) {
  const enrolled = useMsEnrolment();
  const { loading: frameworksLoading } = useFrameworks();
  const { title, subtitle } = TITLES[tab];
  return (
    <div style={pageContainer}>
      <h1 style={pageHeading}>{title}</h1>
      <p style={{ ...pageSubtitle, marginBottom: 16 }}>{subtitle}</p>
      {enrolled.length === 0 && frameworksLoading ? <LoadingBlock /> : enrolled.length === 0 ? (
        <div style={emptyFrame}>
          <div style={text.itemTitle}>No management-system standard enrolled</div>
          <p style={{ ...text.hint, margin: "6px 0 0" }}>These registers serve ISO/IEC 27001, ISO 9001, ISO/IEC 42001, ISO 45001 and ISO 14001. Add one of them on Services &amp; billing to start.</p>
        </div>
      ) : tab === "overview" ? <Overview enrolled={enrolled} />
        : tab === "context" ? <ContextRegister />
        : tab === "objectives" ? <ObjectivesRegister />
        : tab === "audits" ? <AuditProgramme />
        : tab === "reviews" ? <ManagementReviews />
        : tab === "competence" ? <CompetenceRegister />
        : tab === "aspects" ? <AspectsRegister />
        : tab === "hazards" ? <HazardsRegister />
        : <ObligationsRegister />}
    </div>
  );
}

const CARDS: Array<{ tab: MsTab; key: keyof NonNullable<ReturnType<typeof useMsOverview>["data"]>["counts"]; relevance: string; clause: string }> = [
  { tab: "audits", key: "audits", relevance: "internal_audit", clause: "9.2" },
  { tab: "reviews", key: "reviews", relevance: "management_review", clause: "9.3" },
  { tab: "objectives", key: "objectives", relevance: "objectives", clause: "6.2 / 9.1" },
  { tab: "context", key: "context", relevance: "context", clause: "4.1 / 4.2" },
  { tab: "competence", key: "competence", relevance: "competence", clause: "7.2" },
  { tab: "aspects", key: "aspects", relevance: "aspects", clause: "ISO 14001 6.1.2" },
  { tab: "hazards", key: "hazards", relevance: "hazards", clause: "ISO 45001 6.1.2" },
  { tab: "obligations", key: "obligations", relevance: "obligations", clause: "6.1.3 / 9.1.2" },
];

function Overview({ enrolled }: { enrolled: MsFrameworkId[] }) {
  const { data, loading, error } = useMsOverview();
  if (loading) return <LoadingBlock />;
  if (error || !data) return <p style={{ ...text.body, color: theme.color.dangerText }}>{error}</p>;
  const actions = data.attention.filter((a) => a.severity !== "low");
  const suggestions = data.attention.filter((a) => a.severity === "low");
  return (
    <>
      <p style={{ ...text.hint, marginTop: 0 }}>For {enrolled.map((f) => MS_LABELS[f]).join(", ")}.</p>
      <DimensionHeading count={actions.length}>Needs attention</DimensionHeading>
      <AttentionList items={actions} empty="Nothing on these registers needs action now." />
      {suggestions.length > 0 && (
        <>
          <DimensionHeading as="h3" count={suggestions.length}>Suggested</DimensionHeading>
          <AttentionList items={suggestions} />
        </>
      )}
      <DimensionHeading>Registers</DimensionHeading>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(264px, 1fr))", gap: 12 }}>
        {CARDS.filter((c) => data.relevant[c.relevance]).map((c) => (
          <a key={c.tab} href={`/management-system/${c.tab}`} style={{ textDecoration: "none", color: "inherit" }}>
            <SheetCard label={TITLES[c.tab].title} reference={c.clause} padding={12}>
              <div style={text.stat}>{data.counts[c.key]}</div>
              <div style={text.hint}>{data.counts[c.key] === 1 ? "record" : "records"}</div>
            </SheetCard>
          </a>
        ))}
      </div>
    </>
  );
}
